<script setup>
import { computed, ref, shallowRef } from 'vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest, errorMessage } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { useListResource } from '@/composables/useListResource';
import { useSchoolYears } from '@/composables/useSchoolYears';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { useToast } from '@/composables/useToast';
import { formatDate, isoDate } from '@/utils/format';

useSessionGuard();

const headers = [
  { title: 'School year', key: 'school_year' },
  { title: 'Days of classes', key: 'class_start_date', sortable: false },
  { title: 'Grading periods', key: 'syType' },
  { title: 'Status', key: 'status' },
];

// Same list as the header's year picker; reloading it refreshes both.
const schoolYears = useSchoolYears();
const { items, loading, error, load } = useListResource(() => schoolYears.load({ keepSelection: true }));
load();

const editing = shallowRef(null); // null = add
const formOpen = shallowRef(false);
const form = ref({ from: null, to: '', syType: 0, status: 1, classStart: '', classEnd: '' });

const yearOptions = computed(() => {
  const now = new Date().getFullYear();
  const list = Array.from({ length: 10 }, (_, i) => now + i);
  const current = Number(editing.value?.school_year_from);
  if (current && !list.includes(current)) list.unshift(current);
  return list;
});
const fields = computed(() => [
  { key: 'from', label: 'From', type: 'select', required: true, items: yearOptions.value.map((y) => ({ title: String(y), value: y })) },
  { key: 'to', label: 'To', disabled: true },
  {
    key: 'classStart',
    label: 'First day of classes',
    type: 'date',
    required: classDatesRequired.value,
    rules: [withinYear, bothOrNeither],
  },
  {
    key: 'classEnd',
    label: 'Last day of classes',
    type: 'date',
    required: classDatesRequired.value,
    rules: [withinYear, bothOrNeither, (v) => !v || !form.value.classStart || v > form.value.classStart || 'Must be after the first day.'],
    description: classDatesRequired.value ? '' : 'Optional for this older school year, but set both or neither.',
  },
  {
    key: 'syType',
    label: 'Grading periods',
    type: 'select',
    span: 2,
    items: [
      { title: '4 quarters', value: 0 },
      { title: '3 terms', value: 1 },
    ],
  },
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    span: 2,
    hidden: !editing.value,
    description: 'Updates are only allowed while the school year is Active.',
    items: [
      { title: 'Active', value: 1 },
      { title: 'Inactive', value: 0 },
    ],
  },
]);

// Required for a new year and for one that already has them. A year added
// before the dates were asked for may stay without them.
const classDatesRequired = computed(
  () => !editing.value || !!(editing.value.class_start_date || editing.value.class_end_date),
);
const bothOrNeither = (v) =>
  !!v || !(form.value.classStart || form.value.classEnd) || 'Set both days, or leave both empty.';

// The days of classes fall within the school year, e.g. 2026–2027.
const withinYear = (v) => {
  const from = Number(form.value.from);
  if (!v || !from) return true;
  const year = Number(v.slice(0, 4));
  return (year >= from && year <= from + 1) || `Must be within ${from}–${from + 1}.`;
};

// "To" always follows "From".
const values = computed({
  get: () => form.value,
  set: (v) => (form.value = { ...v, to: v.from ? String(Number(v.from) + 1) : '' }),
});

function openForm(year = null) {
  editing.value = year;
  const from = year ? Number(year.school_year_from) : null;
  form.value = {
    from,
    to: from ? String(from + 1) : '',
    syType: year?.syType != null ? Number(year.syType) : 0,
    status: year ? Number(year.status) : 1,
    classStart: isoDate(year?.class_start_date),
    classEnd: isoDate(year?.class_end_date),
  };
  formOpen.value = true;
}

const { busy, run } = useAsyncAction();
const { confirm } = useConfirm();
const toast = useToast();

async function save() {
  const { from, syType, status, classStart, classEnd } = form.value;
  const year = editing.value;
  if (year) {
    if (Number(status) !== 1) {
      toast.error('Set the school year to Active to save changes.');
      return;
    }
    const ok = await confirm({
      title: 'Save changes?',
      description: `School year ${from}–${Number(from) + 1} will be updated.`,
      confirmText: 'Save changes',
    });
    if (!ok) return;
  }
  const data = {
    school_year_from: from,
    school_year_to: Number(from) + 1,
    syType,
    // null, not '', when left empty on an older year: the server skips it.
    class_start_date: classStart || null,
    class_end_date: classEnd || null,
    ...(year ? { status } : {}),
  };
  const done = await run(
    () =>
      year
        ? apiRequest('patch', `/enroll-student/updateSchoolYear/${year.id}`, data)
        : apiRequest('post', '/enroll-student/addSchoolYear', data),
    { success: year ? 'School year updated.' : 'School year added.', error: 'Could not save the school year.' },
  );
  if (done) {
    formOpen.value = false;
    load();
  }
}

// Quarter dates: when each grading period starts and ends. LARDO alerts are
// filed under the period their absence falls in, for the quarterly summary.
const quartersYear = shallowRef(null);
const quartersOpen = shallowRef(false);
const periods = shallowRef([]); // ['1st Quarter', …]
const quarterForm = ref({});

async function openQuarters(year) {
  let rows;
  try {
    rows = await apiRequest('get', `/enroll-student/getSchoolYearQuarters/${year.id}`);
  } catch (e) {
    toast.error(errorMessage(e, 'Could not load the quarter dates.'));
    return;
  }
  quartersYear.value = year;
  classDays.value = { start: isoDate(year.class_start_date), end: isoDate(year.class_end_date) };
  periods.value = rows.map((r) => r.quarter);
  quarterForm.value = Object.fromEntries(
    rows.flatMap((r, i) => [
      [`start${i}`, r.start_date || ''],
      [`end${i}`, r.end_date || ''],
    ]),
  );
  quartersOpen.value = true;
}

// Quarter dates stay within the year's days of classes, when those are set.
const classDays = shallowRef({ start: '', end: '' });
const withinClassDays = (v) => {
  const { start, end } = classDays.value;
  if (!v) return true;
  if (start && v < start) return `Must be on or after the first day of classes (${formatDate(start)}).`;
  if (end && v > end) return `Must be on or before the last day of classes (${formatDate(end)}).`;
  return true;
};

// Either every date is set or none is (none clears them).
const anyQuarterDate = computed(() => Object.values(quarterForm.value).some(Boolean));
const quarterFields = computed(() =>
  periods.value.flatMap((period, i) => {
    const filledIfAny = (v) => !anyQuarterDate.value || !!v || 'Set every date, or clear them all.';
    const afterPrevious = (v) => {
      const previousEnd = quarterForm.value[`end${i - 1}`];
      return !v || !previousEnd || v > previousEnd || 'Must start after the previous period ends.';
    };
    const afterStart = (v) => {
      const start = quarterForm.value[`start${i}`];
      return !v || !start || v >= start || 'Must end on or after the start date.';
    };
    return [
      { key: `start${i}`, label: `${period} starts`, type: 'date', rules: [filledIfAny, afterPrevious, withinClassDays] },
      { key: `end${i}`, label: `${period} ends`, type: 'date', rules: [filledIfAny, afterStart, withinClassDays] },
    ];
  }),
);

async function saveQuarters() {
  const f = quarterForm.value;
  const quarters = anyQuarterDate.value
    ? periods.value.map((quarter, i) => ({ quarter, start_date: f[`start${i}`], end_date: f[`end${i}`] }))
    : [];
  const done = await run(
    () => apiRequest('put', `/enroll-student/setSchoolYearQuarters/${quartersYear.value.id}`, { quarters }),
    { success: 'Quarter dates saved.', error: 'Could not save the quarter dates.' },
  );
  if (done) quartersOpen.value = false;
}
</script>

<template>
  <UiTableCard
    title="School years"
    description="The active school year is the default in the year picker."
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    :searchable="false"
    add-label="Add school year"
    empty-text="No school years yet."
    row-label="school_year"
    :row-actions="
      (year) => [
        { title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => openForm(year) },
        { title: 'Quarter dates', icon: 'mdi-calendar-range', onClick: () => openQuarters(year) },
      ]
    "
    @add="openForm()"
  >
    <template #[`item.school_year`]="{ item }">
      <span class="font-weight-medium">{{ item.school_year }}</span>
    </template>
    <template #[`item.class_start_date`]="{ item }">
      <span v-if="item.class_start_date && item.class_end_date">
        {{ formatDate(item.class_start_date) }} – {{ formatDate(item.class_end_date) }}
      </span>
      <span v-else class="text-medium-emphasis">Not set</span>
    </template>
    <template #[`item.syType`]="{ item }">{{ Number(item.syType) === 1 ? '3 terms' : '4 quarters' }}</template>
    <template #[`item.status`]="{ item }">
      <UiBadge :variant="Number(item.status) === 1 ? 'green' : 'secondary'">
        {{ Number(item.status) === 1 ? 'Active' : 'Inactive' }}
      </UiBadge>
    </template>
  </UiTableCard>

  <UiFormDialog
    v-model="formOpen"
    :title="editing ? 'Edit school year' : 'Add school year'"
    layout="grid"
    v-model:values="values"
    :fields="fields"
    :saving="busy"
    :submit-text="editing ? 'Save changes' : 'Add school year'"
    @submit="save"
  />

  <UiFormDialog
    v-model="quartersOpen"
    title="Quarter dates"
    :description="`School year ${quartersYear?.school_year || ''}. LARDO alerts are filed under the period their absence falls in.`"
    layout="grid"
    v-model:values="quarterForm"
    :fields="quarterFields"
    :saving="busy"
    submit-text="Save dates"
    @submit="saveQuarters"
  />
</template>
